import { Dialog, DialogTrigger } from "@radix-ui/react-dialog";
import { DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { Share2, SquareGanttChart } from "lucide-react";
import {
  QuranMorphologyType,
  QuranWbwIndoPak15ResponseType,
  QuranWbwIndoPak16ResponseType,
  QuranWbwUthmaniResponseType,
} from "@/lib/types";
import { useEffect, useRef, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent, CardHeader } from "./card";
import { type CarouselApi } from "@/components/ui/carousel";
import { cn } from "@/lib/helpers/utils";
import {
  amiri,
  indoPak,
  interFont,
  NastaleeqHafs,
  uthmaniHafs,
} from "@/app/fonts";
import { useQuranOptions } from "@/app/providers/QuranContextProvider";
import QuranWbwTranslations from "./quran-wbw-translation";
import { Inter } from "next/font/google";
import { Separator } from "./separator";
import { useTheme } from "@/app/providers/QuranThemeProvider";
export default function QuranMorphologyView({
  morphology,
  word,
}: {
  morphology: QuranWbwUthmaniResponseType["morphology"];
  word:
    | QuranWbwUthmaniResponseType
    | QuranWbwIndoPak16ResponseType
    | QuranWbwIndoPak15ResponseType;
}) {
  const { quranMushaf } = useQuranOptions();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const { colorMode } = useTheme();

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <Dialog>
      <DialogTrigger asChild className="w-full">
        <div className="hover:bg-primary/20 rounded-md p-[.4rem] cursor-default flex flex-row border-0">
          <span className="pr-2">
            <SquareGanttChart className="h-5 w-5 text-primary" />
          </span>
          <span className="relative  text-text">Morphology</span>
        </div>
      </DialogTrigger>
      <DialogContent className="min-w-10/12 sm:min-w-xl max-h-11/12 rounded-xl p-0">
        <div className="bg-primary/15 dialog-test-class pb-0 rounded-lg min-w-full sm:min-w-xl">
          <DialogHeader className="p-5">
            <DialogTitle className="text-center">
              <div className="text-text w-full">
                <div className="flex flex-row justify-evenly items-center">
                  <div
                    className="p-2 text-center text-text"
                    style={{ direction: "rtl" }}
                  >
                    {Array.from({ length: word.morphology!!.length }).map(
                      (_, i) => (
                        <span
                          key={"word-part-" + i}
                          className={cn(
                            current - 1 == morphology!![i].part - 1
                              ? colorMode == "dark"
                                ? "text-red-400"
                                : "text-red-800"
                              : "text-text",

                            `${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : NastaleeqHafs.className} text-right font-bold text-4xl leading-10 `,
                          )}
                        >
                          {current - 1 == morphology!![i].part - 1
                            ? morphology!![i].color.replace("۟", "ْ")
                            : word.morphology!![i].color.replace("۟", "ْ")}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <Carousel setApi={setApi} className="">
            <CarouselPrevious className="size-9" />
            <CarouselContent className="w-full p-0 -ml-2 bg-transparent ">
              {Array.from({ length: morphology!!.length }).map((_, index) => (
                <CarouselItem key={"morphology-" + index} className="w-full">
                  {morphology && (
                    <QuranMorphologyItem
                      morphology={morphology[index]}
                      word={word}
                    />
                  )}
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselNext className="size-9" />
          </Carousel>
          <Card className="w-full shadow-none border-none bg-transparent p-2 rounded-none">
            <div className="w-full flex flex-row justify-center ">
              {Array.from({ length: count }).map((_, i) => (
                <div
                  key={"dot-" + i}
                  className={cn(
                    "rounded-full h-2 w-2 m-1",
                    i == current - 1 ? " bg-primary" : " bg-text/50",
                  )}
                ></div>
              ))}
            </div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const QuranMorphologyItem: React.FC<{
  morphology: QuranMorphologyType;
  word:
    | QuranWbwUthmaniResponseType
    | QuranWbwIndoPak16ResponseType
    | QuranWbwIndoPak15ResponseType;
}> = ({ morphology, word }) => {
  const { quranMushaf } = useQuranOptions();
  return (
    <Card className="w-full h-full shadow-none border-none p-4">
      <CardContent className="flex w-full p-0 ">
        <div className="w-full text-text">
          <div className="flex flex-col-reverse w-full h-auto md:h-[150px] justify-between items-center mb-4 md:flex-row p-3 sm:p-0">
            <div className="mr-2">
              <div className="p-2 ml-2 text-center sm:text-left mt-5 sm:mt-0">
                <div className="text-sm font-bold">
                  DEFINATION:
                  {"  " /*These spaces are important and not optional*/}
                  <span className="font-normal">{morphology.def}</span>
                </div>
                <div className="text-sm font-bold">
                  BUCK-WALTER:
                  {"  " /*These spaces are important and not optional*/}
                  <span className="font-normal">{morphology.buckwalter}</span>
                </div>
              </div>
            </div>
            <div
              className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : NastaleeqHafs.className}
                    flex flex-row justify-center items-center text-right font-bold text-xl  h-full bg-primary/10 mr-2 rounded-md p-4 w-28  sm:w-[150px] sm:h-[150px] `}
            >
              <div className="flex flex-col gap-3">
                <div className="text-center text-md">
                  {morphology.color.replace("۟", "ْ")}
                </div>
                <div
                  className={`text-sm font-normal text-center ${interFont.className}`}
                >
                  {morphology.features}
                </div>
                <div className="text-lg font-normal text-center relative -top-2">
                  {morphology.features_arabic}
                </div>
              </div>
            </div>
          </div>
          <Separator orientation="horizontal" />

          <div className="p-2 ml-2 mr-2 text-xs mt-3">
            {morphology.prounoun_detail && (
              <div className="w-full flex flex-row justify-between items-center leading-8">
                <div className="leading-normal pr-3 text-md">
                  {morphology.prounoun_detail}
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-lg text-right`}
                >
                  {morphology.pronoun_detail_arabic}
                </div>
              </div>
            )}

            {morphology.case && (
              <div className="w-full flex flex-row justify-between items-center leading-8">
                <div className="leading-normal pr-3 text-md">
                  {morphology.case}
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-lg text-right`}
                >
                  {morphology.case_arabic}
                </div>
              </div>
            )}

            {morphology["D/I"] && (
              <div className="w-full flex flex-row justify-between items-center leading-8">
                <div className="leading-normal pr-3 text-md">
                  {morphology["D/I"]}
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-lg text-right`}
                >
                  {morphology["D/I_arab"]}
                </div>
              </div>
            )}

            {morphology.voice && (
              <div className="w-full flex flex-row justify-between items-center leading-8">
                <div className="leading-normal pr-3 text-md">
                  {morphology.voice}
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-lg text-right`}
                >
                  {morphology.voice_arabic}
                </div>
              </div>
            )}

            {morphology.participle && (
              <div className="w-full flex flex-row justify-between items-center leading-8">
                <div className="leading-normal pr-3 text-md">
                  {morphology.participle}
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-lg text-right`}
                >
                  {morphology.participle_arabic}
                </div>
              </div>
            )}

            {morphology.verb_type && (
              <div className="w-full flex flex-row justify-between items-center leading-8">
                <div className="leading-normal pr-3 text-md">
                  {morphology.verb_type}
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-lg text-right`}
                >
                  {morphology.verb_type_arabic}
                </div>
              </div>
            )}

            {morphology.mood && (
              <div className="w-full flex flex-row justify-between items-center leading-8">
                <div className="leading-normal pr-3 text-mdM">
                  {morphology.mood}
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-lg text-right`}
                >
                  {morphology.mood_arabic}
                </div>
              </div>
            )}

            {morphology.form && (
              <div className="w-full flex flex-row justify-center items-center  gap-3 leading-10 mt-2">
                <div className="text-right text-xl">
                  ({morphology.form})
                  <span className={`text-primary ${interFont.className}`}>
                    {" "}
                    ——{" "}
                  </span>
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-2xl text-center`}
                >
                  {morphology.pair1}
                  <span className={`text-primary ${interFont.className}`}>
                    {" "}
                    ——{" "}
                  </span>
                </div>
                <div
                  className={`${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-2xl text-left`}
                >
                  {` ${morphology.pair2} `}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// <Dialog>
// <DialogTrigger className="w-full">
{
  /*     <div className="hover:bg-primary/20 ounded-md p-[.4rem] cursor-default flex flex-row"> */
}
{
  /*       <span className="pr-2"> */
}
{
  /*         <SquareGanttChart className="h-5 w-5 text-primary" /> */
}
{
  /*       </span> */
}
{
  /*       <span className="relative text-text">Morphology</span> */
}
{
  /*     </div> */
}
{
  /*   </DialogTrigger> */
}
{
  /*   <DialogContent className="justify-center  p-0 border-card  rounded-lg overflow-hidden min-w-11/12 md:min-w-4xl"> */
}
{
  /*     <DialogHeader className="bg-primary/10 p-10 w-full"> */
}
{
  /*       <DialogTitle className="text-center w-full"> */
}
{
  /*         <div className="text-text w-full"> */
}
{
  /*           <div className="flex flex-row justify-evenly items-center"> */
}
{
  /*             <div */
}
{
  /*               className="p-2 text-center text-text" */
}
{
  /*               style={{ direction: "rtl" }} */
}
{
  /*             > */
}
{
  /*               {Array.from({ length: word.morphology!!.length }).map( */
}
{
  /*                 (_, i) => ( */
}
{
  /*                   <span */
}
{
  /*                     key={"word-part-" + i} */
}
{
  /*                     className={cn( */
}
{
  /*                       current - 1 == morphology!![i].part - 1 */
}
{
  /*                         ? colorMode == "dark" */
}
{
  /*                           ? "text-red-400" */
}
{
  /*                           : "text-red-800" */
}
{
  /*                         : "text-text", */
}
{
  /*                       `${quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16" ? indoPak.className : uthmaniHafs.className} text-right font-bold text-4xl leading-10 `, */
}
{
  /*                     )} */
}
{
  /*                   > */
}
{
  /*                     {current - 1 == morphology!![i].part - 1 */
}
{
  /*                       ? morphology!![i].color */
}
{
  /*                       : word.morphology!![i].color} */
}
{
  /*                   </span> */
}
{
  /*                 ), */
}
{
  /*               )} */
}
{
  /*             </div> */
}
{
  /*           </div> */
}
{
  /*         </div> */
}
{
  /*       </DialogTitle> */
}
{
  /*     </DialogHeader> */
}
{
  /*     <Carousel setApi={setApi} className="min-w-full md:min-w-3xl"> */
}
{
  /*       <CarouselNext /> */
}
{
  /*       <CarouselContent className="w-full p-0 -ml-2 bg-card"> */
}
{
  /*         {Array.from({ length: morphology!!.length }).map((_, index) => ( */
}
{
  /*           <CarouselItem key={"morphology-" + index} className="w-full "> */
}
{
  /*             {morphology && ( */
}
{
  /*               <QuranMorphologyItem */
}
{
  /*                 morphology={morphology[index]} */
}
{
  /*                 word={word} */
}
{
  /*               /> */
}
{
  /*             )} */
}
{
  /*           </CarouselItem> */
}
{
  /*         ))} */
}
{
  /*       </CarouselContent> */
}
{
  /*       <CarouselPrevious /> */
}
{
  /*     </Carousel> */
}
{
  /*     <Card className="w-full shadow-none border-none bg-primary/10 p-2 rounded-none"> */
}
{
  /*       <div className="w-full flex flex-row justify-center "> */
}
{
  /*         {Array.from({ length: count }).map((_, i) => ( */
}
{
  /*           <div */
}
{
  /*             key={"dot-" + i} */
}
{
  /*             className={cn( */
}
{
  /*               "rounded-full h-2 w-2 m-1", */
}
{
  /*               i == current - 1 ? " bg-primary" : " bg-text/50", */
}
{
  /*             )} */
}
{
  /*           ></div> */
}
{
  /*         ))} */
}
{
  /*       </div> */
}
{
  /*     </Card> */
}
{
  /*   </DialogContent> */
}
{
  /* </Dialog> */
}
